/* ===========================================================
   EVM · Carrusel editorial
   Arrastre con el ratón, deslizamiento con el dedo, flechas,
   contador, barra de avance y pase automático opcional.
   Sin dependencias.
   =========================================================== */

/* Con dos instancias de la sección en la misma página, el navegador
   ejecuta este archivo dos veces: si ya está definido, no se repite
   nada (ni la clase ni los listeners de documento). */
if (!customElements.get('evm-carrusel')) {

class EvmCarrusel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.evm-car__track');
    this.viewport = this.querySelector('.evm-car__viewport');
    if (!this.track || !this.viewport) return;

    this.slides = Array.from(this.querySelectorAll('.evm-car__slide'));
    if (!this.slides.length) return;

    this.prevBtn = this.querySelector('.evm-car__arrow--prev');
    this.nextBtn = this.querySelector('.evm-car__arrow--next');
    this.current = this.querySelector('.evm-car__counter-current');
    this.progress = this.querySelector('.evm-car__progress-bar');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = 0;
    this.dragging = false;
    this.moved = 0;
    this.autoTimer = null;
    this.paused = false;

    this.onScroll = this.throttle(this.update.bind(this), 60);
    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    // Arrastre con ratón / lápiz. El dedo ya usa el scroll nativo.
    this.viewport.addEventListener('pointerdown', this.onDown);

    // El pase automático se pausa mientras se mira o se navega
    ['pointerenter', 'focusin'].forEach((e) =>
      this.addEventListener(e, () => { this.paused = true; })
    );
    ['pointerleave', 'focusout'].forEach((e) =>
      this.addEventListener(e, () => { this.paused = false; })
    );
    this.track.addEventListener('touchstart', () => this.stopAuto(), { passive: true });

    this.update();

    if (this.dataset.autoplay === 'true' && !this.reduceMotion) this.startAuto();
  }

  disconnectedCallback() {
    this.stopAuto();
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.viewport) this.viewport.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
  }

  /* ---------- Medidas ---------- */
  get stride() {
    if (this.slides.length < 2) return this.slides[0].getBoundingClientRect().width;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  /* ---------- Estado ---------- */
  update() {
    const stride = this.stride || 1;
    this.index = Math.round(this.track.scrollLeft / stride);
    this.index = Math.max(0, Math.min(this.index, this.slides.length - 1));

    if (this.current) this.current.textContent = String(this.index + 1);

    const ratio = this.maxScroll > 0 ? this.track.scrollLeft / this.maxScroll : 0;
    this.style.setProperty('--car-progress', ratio.toFixed(4));

    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= this.maxScroll - 2;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
  }

  step(dir) {
    this.stopAuto();
    this.goTo(this.index + dir);
  }

  goTo(i) {
    const target = Math.max(0, Math.min(i, this.slides.length - 1));
    this.track.scrollTo({
      left: target * this.stride,
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });
  }

  /* ---------- Arrastre ---------- */
  onDown(e) {
    // Solo ratón o lápiz: en táctil el navegador ya lo hace mejor
    if (e.pointerType === 'touch' || e.button !== 0) return;
    this.dragging = true;
    this.moved = 0;
    this.startX = e.clientX;
    this.startScroll = this.track.scrollLeft;
    this.viewport.classList.add('is-dragging');
    this.stopAuto();
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp, { once: true });
  }

  onMove(e) {
    if (!this.dragging) return;
    const dx = e.clientX - this.startX;
    this.moved = Math.abs(dx);
    this.track.scrollLeft = this.startScroll - dx;
  }

  onUp() {
    if (!this.dragging) return;
    this.dragging = false;
    window.removeEventListener('pointermove', this.onMove);

    // Al soltar, encaja en la diapositiva más cercana
    const stride = this.stride || 1;
    const nearest = Math.round(this.track.scrollLeft / stride);
    this.goTo(nearest);

    // Deja pasar un instante antes de reactivar los enlaces,
    // para que un arrastre no acabe abriendo una ficha por error
    window.setTimeout(() => this.viewport.classList.remove('is-dragging'), this.moved > 6 ? 60 : 0);
  }

  /* ---------- Pase automático ---------- */
  startAuto() {
    this.stopAuto();
    const secs = parseInt(this.dataset.autoplaySpeed, 10) || 6;
    this.autoTimer = window.setInterval(() => {
      if (this.paused || !this.isVisible()) return;
      const next = this.track.scrollLeft >= this.maxScroll - 2 ? 0 : this.index + 1;
      this.goTo(next);
    }, secs * 1000);
  }

  stopAuto() {
    if (this.autoTimer) {
      window.clearInterval(this.autoTimer);
      this.autoTimer = null;
    }
  }

  isVisible() {
    const r = this.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  /* ---------- Utilidad ---------- */
  throttle(fn, wait) {
    let last = 0;
    let timer = null;
    return (...args) => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn(...args);
      } else if (!timer) {
        timer = window.setTimeout(() => {
          last = Date.now();
          timer = null;
          fn(...args);
        }, wait - (now - last));
      }
    };
  }

  revealBlock(blockId) {
    const slide = this.querySelector(`[data-shopify-editor-block*="${blockId}"]`);
    if (!slide) return;
    this.stopAuto();
    this.goTo(this.slides.indexOf(slide));
  }
}

if (!customElements.get('evm-carrusel')) {
  customElements.define('evm-carrusel', EvmCarrusel);
}

/* ---------- Editor de temas ---------- */
document.addEventListener('shopify:block:select', (event) => {
  const host = event.target.closest('evm-carrusel');
  if (host && typeof host.revealBlock === 'function') host.revealBlock(event.detail.blockId);
});

document.addEventListener('shopify:section:select', (event) => {
  const host = event.target.querySelector('evm-carrusel');
  if (host && typeof host.update === 'function') host.update();
});

}
